import React, { useEffect, useMemo, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Grid,
    Typography,
    Stack,
    CircularProgress,
    Alert,
} from "@mui/material";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import IbanInput from "qubCommon/Inputs/IbanInput";
import AfmInput from "qubCommon/Inputs/AfmInput";

// Optional: if you have a WS validation similar to the applicant IBAN one
// import { customValidateIban } from "apis/controllers/externalData";

const ChangeChildrenIbansDialog = ({
    open,
    onClose,
    members,         // BackendChangeRequest
    initialIbans,    // function(childId) => { iban, afm } from childrenIbans (optional)
    onSave,                 // async (newIbans) => void
}) => {
    const [submitting, setSubmitting] = useState(false);

    const methods = useForm({
        defaultValues: { newIbans: [] },
        mode: "onChange",
    });

    const { control, reset, getValues } = methods;

    const { fields, replace } = useFieldArray({
        control,
        name: "newIbans",
        keyName: "key",
    });

    // inside your component
    const initialFromMainForm = React.useCallback(
        (childId) => {
            const rows = initialIbans || []; // <- your array above
            const match = rows.find((r) => r?.id === childId);
            return match ? { iban: match.iban ?? "", afm: match.afm ?? "" } : { iban: "", afm: "" };
        },
        [initialIbans]
    );


    // Build the rows when backend tells you which children must change
    useEffect(() => {
        if (!open) return;

        const rows =
            members?.map((c) => ({
                childId: c.id,
                iban: "",
                afm: "",
            })) ?? [];

        reset({ newIbans: rows });
    }, [open, members, reset]);

    const rowsMetaById = useMemo(() => {
        const map = new Map();
        (members || []).forEach((c) => map.set(c.childId, c));
        return map;
    }, [members]);

    const handleSubmit = async () => {
        if (submitting) return;
        try {
            setSubmitting(true);
            const payload = getValues("newIbans") || [];
            await onSave(payload); // submit ONLY these (1–2) rows
            onClose();
        } catch (e) {
            console.error("Failed saving newIbans:", e);
        } finally {
            setSubmitting(false);
        }
    };

    const isBusy = submitting;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <FormProvider {...methods}>
                <DialogTitle variant="h4">Διόρθωση IBAN/ΑΦΜ Τέκνων</DialogTitle>

                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        Ο έλεγχος από το σύστημα απαιτεί ενημέρωση στοιχείων για{" "}
                        {fields.length} τέκνο(α). Παρακαλώ συμπληρώστε τα νέα στοιχεία.
                    </Alert>
                    <Stack gap={2} sx={{ mt: 1 }}>


                        {fields.map((row, index) => {
                            const member = members?.find((m) => m.id === row.childId);
                            const oldValues = initialFromMainForm(row.childId);

                            return (
                                <Grid
                                    key={row.key}
                                    container
                                    spacing={2}
                                    sx={{
                                        p: 2,
                                    }}
                                >
                                    <Grid item xs={12}>
                                        <Typography variant='h4'>
                                            {member.lastName} {member.firstName}
                                        </Typography>
                                    </Grid>
                                    {/* OLD VALUES */}
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2">Προηγούμενο IBAN</Typography>
                                        <Typography
                                            sx={{ textDecoration: "line-through", fontSize: 14 }}
                                        >
                                            {oldValues.iban || "-"}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" mt={1}>
                                            Προηγούμενο ΑΦΜ
                                        </Typography>
                                        <Typography
                                            sx={{ textDecoration: "line-through", fontSize: 14 }}
                                        >
                                            {oldValues.afm || "-"}
                                        </Typography>
                                    </Grid>

                                    {/* NEW VALUES */}
                                    <Grid item xs={12} md={6}>
                                        <IbanInput
                                            name={`newIbans.${index}.iban`}
                                            label="Νέο IBAN"
                                            disabled={isBusy}
                                            defaultFromProps={false}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <AfmInput
                                            name={`newIbans.${index}.afm`}
                                            label="Νέο ΑΦΜ"
                                            disabled={isBusy}
                                        />
                                    </Grid>
                                </Grid>
                            );
                        })}
                    </Stack>
                </DialogContent>

                <DialogActions>
                    <Button onClick={onClose} variant="contained" color="error" disabled={isBusy}>
                        Ακύρωση
                    </Button>

                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        color="primary"
                        disabled={isBusy || fields.length === 0}
                        startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : null}
                    >
                        {submitting ? "Υποβολή..." : "Υποβολή"}
                    </Button>
                </DialogActions>
            </FormProvider>
        </Dialog >
    );
};

export default ChangeChildrenIbansDialog;
